import React from "react";

/**
 * Types d'étape et modes de panneau, décrits par un PICTOGRAMME plutôt que par
 * une ligne de liste déroulante.
 *
 * POURQUOI DES DESSINS. Un auteur ne choisit pas « quiz_mouvant » dans une
 * liste : il choisit une forme d'écran. Un croquis de trois cartes retournées
 * ou de trois grands chiffres au sol dit en un coup d'œil ce que la liste
 * mettait une phrase à expliquer — et surtout ce que le mot ne disait pas du
 * tout, à savoir à quoi ça ressemble une fois projeté.
 *
 * Les croquis sont volontairement schématiques (aplats, pas de texte) : ils
 * représentent une MISE EN PAGE, pas un contenu, et doivent rester lisibles à
 * 64 px de large dans le storyboard.
 */

// Palette des croquis : reprend les tons du module territoire pour que le
// builder et l'écran projeté parlent la même langue.
const INK = "#94a3b8";
const FILL = "#e2e8f0";
const ACCENT = "#54a8ba";

function Sketch({ children, title }) {
  return (
    <svg viewBox="0 0 64 40" className="step-sketch" role="img" aria-label={title}>
      <rect x="0.5" y="0.5" width="63" height="39" rx="4" fill="#0a1a24" stroke={INK} strokeOpacity="0.4" />
      {children}
    </svg>
  );
}

export const STEP_KINDS = [
  {
    id: "message",
    label: "Message",
    hint: "Un texte, éventuellement illustré, à côté de la carte.",
    sketch: (
      <Sketch title="Message">
        <rect x="5" y="6" width="20" height="28" rx="2" fill={FILL} fillOpacity="0.9" />
        <rect x="8" y="10" width="14" height="7" rx="1" fill={ACCENT} fillOpacity="0.5" />
        <rect x="8" y="20" width="14" height="2" rx="1" fill={INK} />
        <rect x="8" y="24" width="11" height="2" rx="1" fill={INK} />
        <rect x="8" y="28" width="13" height="2" rx="1" fill={INK} />
      </Sketch>
    ),
  },
  {
    id: "slide",
    label: "Diapo",
    hint: "Une image au format 16:9, seule, pour projeter un visuel préparé ailleurs.",
    sketch: (
      <Sketch title="Diapo">
        <rect x="8" y="6" width="48" height="27" rx="2" fill={FILL} fillOpacity="0.9" />
        <path d="M14 28 L23 18 L30 24 L39 13 L50 28 Z" fill={ACCENT} fillOpacity="0.75" />
        <circle cx="45" cy="12" r="2.5" fill={ACCENT} />
      </Sketch>
    ),
  },
  {
    id: "graph",
    label: "Graphique",
    hint: "Un ou plusieurs composants de score, à côté de la carte.",
    sketch: (
      <Sketch title="Graphique">
        <rect x="5" y="6" width="20" height="28" rx="2" fill={FILL} fillOpacity="0.9" />
        <rect x="9" y="22" width="3" height="8" rx="1" fill={ACCENT} />
        <rect x="14" y="16" width="3" height="14" rx="1" fill={ACCENT} />
        <rect x="19" y="12" width="3" height="18" rx="1" fill={ACCENT} />
      </Sketch>
    ),
  },
  {
    id: "quiz",
    label: "Quizz (cartes)",
    hint: "Des cartes que l'animateur retourne une à une pour révéler la réponse.",
    sketch: (
      <Sketch title="Quizz par cartes">
        <rect x="6" y="12" width="12" height="16" rx="2" fill={FILL} fillOpacity="0.9" />
        <rect x="21" y="12" width="12" height="16" rx="2" fill={ACCENT} fillOpacity="0.75" />
        <rect x="36" y="12" width="12" height="16" rx="2" fill={FILL} fillOpacity="0.55" />
        <path d="M25 18 h4 M25 22 h4" stroke="#0a1a24" strokeWidth="1.5" strokeLinecap="round" />
      </Sketch>
    ),
  },
  {
    id: "quiz_mouvant",
    label: "Quizz mouvant",
    hint: "Des propositions numérotées : les participants se déplacent physiquement dans la salle.",
    sketch: (
      <Sketch title="Quizz mouvant">
        <circle cx="16" cy="20" r="7" fill={ACCENT} fillOpacity="0.8" />
        <circle cx="32" cy="20" r="7" fill={FILL} fillOpacity="0.85" />
        <circle cx="48" cy="20" r="7" fill={FILL} fillOpacity="0.55" />
        <text x="16" y="24" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0a1a24">1</text>
        <text x="32" y="24" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0a1a24">2</text>
        <text x="48" y="24" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0a1a24">3</text>
      </Sketch>
    ),
  },
  {
    id: "explore",
    label: "Explorer",
    hint: "La carte seule : l'animateur commente et manipule librement.",
    sketch: (
      <Sketch title="Explorer">
        <path d="M8 30 L20 14 L30 24 L40 10 L56 26" stroke={ACCENT} strokeWidth="2" fill="none" strokeLinejoin="round" />
        <circle cx="40" cy="10" r="2.5" fill={FILL} />
      </Sketch>
    ),
  },
  {
    id: "points_of_interest",
    label: "Zones d'intérêt",
    hint: "Des points posés sur la carte, que l'animateur ouvre au clic.",
    sketch: (
      <Sketch title="Zones d'intérêt">
        <path d="M6 32 L22 18 L34 26 L58 12" stroke={INK} strokeWidth="1.5" fill="none" strokeOpacity="0.6" />
        <circle cx="22" cy="18" r="4" fill={ACCENT} />
        <circle cx="45" cy="18" r="4" fill={ACCENT} fillOpacity="0.6" />
        <path d="M22 18 v-7 M45 18 v-7" stroke={ACCENT} strokeWidth="1.5" />
      </Sketch>
    ),
  },
];

export function stepKind(kindId) {
  return STEP_KINDS.find((k) => k.id === kindId);
}

/**
 * Les types sans contenu propre : leur section « Contenu » n'a rien à montrer.
 * « explore » laisse la carte nue ; « points_of_interest » a bien un contenu,
 * ce sont les points, qui se posent sur la carte.
 */
export const KINDS_WITHOUT_CONTENT = ["explore"];

export function newStepId() {
  return `s_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * L'étape vide d'un type, telle qu'elle est créée — l'équivalent de
 * `emptyByType` pour les pages (cf. pageKinds.js). Ce registre vit ici, avec
 * les types qu'il complète, et non dans l'éditeur qui s'en sert : plusieurs
 * écrans créent des étapes, aucun n'a à savoir comment les autres le font.
 */
export function defaultStepForKind(kind) {
  const base = {
    step_id: newStepId(), kind, camera: null,
    active_layers: [], layer_settings: {},
    panel_mode: "normal",
    // Le sujet dont l'étape parle. Les étapes qui se suivent avec le même nom
    // forment un chapitre (cf. territory_intro/chapters.js) ; vide = aucun.
    chapter: "",
  };
  // Une diapo est faite pour être projetée en grand : la poser dans un rail de
  // 470 px reviendrait à afficher un 16:9 en timbre-poste. Elle démarre donc
  // en grand panneau centré, que l'auteur reste libre de changer.
  if (kind === "slide") return { ...base, panel_mode: "large", slide: { image_url: "", alt: "" } };
  // Les graphiques d'étape lisent le datapack du territoire (cf. chartSpecs),
  // pas le `gameStatus` d'une partie : `charts`, et non l'ancien `list_graph`
  // des pages de score, que les étapes écrites avant cette bascule portent
  // encore et que le panneau sait toujours afficher.
  if (kind === "graph") return { ...base, charts: [] };
  if (kind === "quiz") return { ...base, quiz: { question: "", cards: [] } };
  if (kind === "quiz_mouvant") {
    return { ...base, quiz_mouvant: { question: "", image_url: "", options: [{ text: "" }, { text: "" }, { text: "" }] } };
  }
  if (kind === "explore") return base;
  if (kind === "points_of_interest") return { ...base, points_of_interest: { points: [] } };
  return { ...base, message: { text: "", media_url: "" } };
}

/**
 * Changer le type d'une étape SANS PERDRE SA VUE. Le cadrage, les couches et
 * la position du panneau appartiennent à la vue ; seul le contenu est propre
 * au type. Perdre un cadrage patiemment réglé parce qu'on transforme un
 * message en quizz serait absurde.
 */
export function stepWithKind(step, kind) {
  const fresh = defaultStepForKind(kind);
  return {
    ...fresh,
    step_id: step.step_id,
    camera: step.camera,
    active_layers: step.active_layers,
    layer_settings: step.layer_settings,
    // Le chapitre dit de quoi PARLE l'étape, pas comment elle le montre :
    // transformer un message en quizz ne change pas le sujet, et le perdre
    // couperait le chapitre en deux à l'insu de l'auteur.
    chapter: step.chapter,
    // La position du panneau survit au changement de type — sauf si l'auteur
    // n'y a jamais touché ET que le nouveau type en réclame une autre (une
    // diapo veut le grand panneau, cf. defaultStepForKind).
    panel_mode: (!step.panel_mode || step.panel_mode === "normal") ? fresh.panel_mode : step.panel_mode,
  };
}

export function stepKindLabel(kindId) {
  return stepKind(kindId)?.label || kindId;
}

/**
 * Modes de panneau, dessinés comme des écrans. Le mode décide de la SURFACE
 * de carte restante — c'est un choix de cadrage autant que de mise en page, et
 * c'est pour ça qu'il est montré et pas listé.
 */
export const PANEL_MODES = [
  {
    id: "normal",
    label: "Panneau latéral",
    hint: "Le contenu occupe un rail à gauche, la carte garde l'essentiel du cadre.",
    sketch: (
      <Sketch title="Panneau latéral">
        <rect x="4" y="4" width="17" height="32" rx="2" fill={FILL} fillOpacity="0.9" />
        <rect x="24" y="4" width="36" height="32" rx="2" fill={ACCENT} fillOpacity="0.28" />
      </Sketch>
    ),
  },
  {
    id: "bottom",
    label: "Bandeau bas",
    hint: "Le contenu s'étale en bas sur toute la largeur, la carte reste entière au-dessus.",
    sketch: (
      <Sketch title="Bandeau bas">
        <rect x="4" y="4" width="56" height="20" rx="2" fill={ACCENT} fillOpacity="0.28" />
        <rect x="4" y="26" width="56" height="10" rx="2" fill={FILL} fillOpacity="0.9" />
      </Sketch>
    ),
  },
  {
    id: "large",
    label: "Grand panneau centré",
    hint: "Le contenu passe au premier plan (une diapo 16:9 y tient en grand), la carte reste en fond.",
    sketch: (
      <Sketch title="Grand panneau centré">
        <rect x="4" y="4" width="56" height="32" rx="2" fill={ACCENT} fillOpacity="0.2" />
        <rect x="7" y="9" width="50" height="22" rx="2" fill={FILL} fillOpacity="0.92" />
      </Sketch>
    ),
  },
  {
    id: "reduced",
    label: "Carte plein cadre",
    hint: "Le panneau démarre masqué : la carte occupe tout l'écran, l'animateur le rouvre s'il en a besoin.",
    sketch: (
      <Sketch title="Carte plein cadre">
        <rect x="4" y="4" width="56" height="32" rx="2" fill={ACCENT} fillOpacity="0.32" />
        <rect x="4" y="30" width="18" height="6" rx="2" fill={FILL} fillOpacity="0.55" />
      </Sketch>
    ),
  },
];

export function panelMode(modeId) {
  return PANEL_MODES.find((m) => m.id === modeId) || PANEL_MODES[0];
}
